<template>
    <!-- Merchant User -->
    <divider-generic
    icon="person"
    :applyBorder="false"
    :title="t('companyPayments.baiPagaMerchant.creatingMerchant.step1.merchantUserDataTitle')"/>
    <!-- Merchant User Name -->
    <input-generic
    id="merchantUserName"
    :params="merchantUserNameParams"
    :inputValue="formData.merchantUserName"
    @onBlur="merchantUserNameValidation"
    @input="[emit('formDataChange', { field: 'merchantUserName', inputValue: $event }), clearFieldError('merchantUserNameParams')]"
    @tooltipLeftButtonClicked="emit('openHelperModal', 2)"/>
    <!-- Merchant User Email -->
    <input-generic
    id="merchantUserEmail"
    :params="merchantUserEmailParams"
    :inputValue="formData.merchantUserEmail"
    @onBlur="merchantUserEmailValidation"
    @input="[emit('formDataChange', { field: 'merchantUserEmail', inputValue: $event.trim()  }), clearFieldError('merchantUserEmailParams')]"/>
    <!-- Merchant User Profile -->
    <text-block-generic
    :params="{
        label: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.profileLabel'),
        content: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.profile')
    }"/>
    <!-- Merchant Data -->
    <divider-generic
    icon="building"
    :title="t('companyPayments.baiPagaMerchant.creatingMerchant.step1.merchantDataTitle')"/>

    <!-- Merchant Name -->
    <input-generic
    id="merchantName"
    :params="merchantNameParams"
    :inputValue="formData.merchantName"
    @onBlur="merchantNameValidation"
    @input="[emit('formDataChange', { field: 'merchantName', inputValue: $event }), clearFieldError('merchantNameParams')]"
    @tooltipLeftButtonClicked="emit('openHelperModal', 3)"/>
    <!-- Merchant Email -->
    <input-generic
    id="merchantEmail"
    :params="merchantEmailParams"
    :inputValue="formData.merchantEmail"
    @onBlur="merchantEmailValidation"
    @input="[emit('formDataChange', { field: 'merchantEmail', inputValue: $event.trim()  }), clearFieldError('merchantEmailParams')]"/>

    <!-- Acceptance Point -->
    <divider-generic
    icon="pin-drop"
    :title="t('companyPayments.baiPagaMerchant.creatingMerchant.step1.acceptancePointTitle')"/>
    <!-- Acceptance Point Name -->
    <input-generic
    id="acceptancePointName"
    :params="acceptancePointNameParams"
    :inputValue="formData.acceptancePointName"
    @onBlur="acceptancePointNameValidation"
    @input="[emit('formDataChange', { field: 'acceptancePointName', inputValue: $event }), clearFieldError('acceptancePointNameParams')]"
    @tooltipLeftButtonClicked="emit('openHelperModal', 4)"/>
    <!-- Acceptance Point Account -->
    <text-block-generic
    :params="{
        label: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.acceptancePointAccountLabel'),
        content: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.acceptancePointAccount')
    }"/>
    <!-- Acceptance Point Msisdn -->
    <input-generic
    id="acceptancePointMsisdn"
    :params="acceptancePointMsisdnParams"
    :inputValue="formData.acceptancePointMsisdn"
    @onBlur="acceptancePointMsisdnValidation"
    @input="[emit('formDataChange', { field: 'acceptancePointMsisdn', inputValue: $event }), clearFieldError('acceptancePointMsisdnParams')]"/>
    <!-- Acceptance Point Notifications -->
    <selector-radio-group
    id="acceptancePointNotificationEnabled"
	:mandatory="acceptancePointRadioGroupParams.mandatory"
    :label="acceptancePointRadioGroupParams.label"
	:errorState="acceptancePointRadioGroupParams.errorState"
	:errorMessage="acceptancePointRadioGroupParams.errorMessage"
    :radioParams="acceptancePointNotificationEnabledParams"
    @clicked="[emit('formDataChange', { field: 'acceptancePointNotificationEnabled', inputValue: $event }), clearFieldError('acceptancePointNotificationEnabledParams')]"/>
    <!-- Conditions -->
    <divider-generic
    icon="pencil-line"
    :title="t('companyPayments.baiPagaMerchant.creatingMerchant.step1.conditionsTitle')"/>
    <div class="message">{{ t('companyPayments.baiPagaMerchant.creatingMerchant.step1.message') }}</div>
    <selector-checkbox
    id="canProceed"
    :label="canProceedParams.label"
    :selected="formData.canProceed"
    :errorState="canProceedParams.errorState"
    :errorMessage="canProceedParams.errorMessage"
    @clicked="[emit('updateCanProceed', { field: 'canProceed', inputValue: $event }), clearFieldError('canProceedParams')]"/>
    <selector-checkbox
    id="transactionsConditions"
    :label="transactionsConditionsParams.label"
    :selected="formData.transactionsConditions"
    :errorState="transactionsConditionsParams.errorState"
    :errorMessage="transactionsConditionsParams.errorMessage"
    @clicked="[emit('updatetransactionsConditions', { field: 'transactionsConditions', inputValue: $event }), clearFieldError('transactionsConditionsParams')]"/>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import InputGeneric from '@/components/generic/inputs/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import SelectorRadioGroup from '@/components/generic/selectors/radio/RadioGroup'
import SelectorCheckbox from '@/components/generic/selectors/checkbox/Checkbox'

/* Vue */
import { ref, watch, computed } from 'vue'

/* Assets */
import { PHONE_NUMBER } from '@/assets/constants/masks'
import { emailValidator, baiPhoneNumberValidator, removeAccents, noSpecialChar } from '@/assets/scripts/validators'

import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['formDataChange', 'updateCanProceed',
'updatetransactionsConditions', 'formErrorsCleared', 'mandatoryListChangeHandler', 'resetShowMandatoryErrorsProp', 'scrollTo', 'openHelperModal'])

/* props received
@formData { Object } - object with info for our current form
@mandatoryList { Array } Array of mandatory keys corresponding with our form
@clearFormErrors { Boolean } - Prop to remove of form errors
@showMandatoryErrors { Boolean } Prop to trigger display of mandatory errors
*/
const props = defineProps({
    formData: Object,
    mandatoryList: Array,
    clearFormErrors: Boolean,
    showMandatoryErrors: Boolean
})

/**********************************************************************
*                           Merchant User Name                        *
**********************************************************************/
const merchantUserNameParams = ref({
    labelText: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.nameLabel'),
    placeholder: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.namePlaceholder'),
    bottomMessage: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.nameBottomMessage'),
    tooltipInfo: {
        text: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.nameTooltip'),
        size: 'medium',
        leftButtonText: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.tooltipLefttBtn'),
        rightButtonText: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.tooltipRightBtn')
    },
    mandatory: true,
    showCounter: true,
    maxLength: 40,
    errorState: false,
    errorMessage: null,
    ourMask: {
        parseInputText: value => { return removeAccents(value) },
        validatorFn: value => { return noSpecialChar(value) }
    }
})

const merchantUserNameValidation = () => {
    if (!!props.formData.merchantUserName && props.formData.merchantUserName.length < 5) {
        merchantUserNameParams.value.errorState = true
        merchantUserNameParams.value.errorMessage = t('genericInputLengthErrorMessage', { minValue: 5, maxValue: 40 })
    }
}

/**********************************************************************
*                           Merchant User Email                       *
**********************************************************************/
const merchantUserEmailParams = ref({
    labelText: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.emailLabel'),
    placeholder: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.emailPlaceholder'),
    mandatory: true,
    errorState: false,
    errorMessage: null
})

const merchantUserEmailValidation = () => {
    if (!!props.formData.merchantUserEmail.length && !emailValidator(props.formData.merchantUserEmail)) {
        merchantUserEmailParams.value.errorState = true
        merchantUserEmailParams.value.errorMessage = t('invalidEmailError')
    }
}

/**********************************************************************
*                             Merchant Name                           *
**********************************************************************/
const merchantNameParams = ref({
    labelText: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.merchantNameLabel'),
    placeholder: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.merchantNamePlaceholder'),
    bottomMessage: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.merchantNameBottomMessage'),
    tooltipInfo: {
        text: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.merchantNameTooltip'),
        size: 'medium',
        leftButtonText: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.tooltipLefttBtn'),
        rightButtonText: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.tooltipRightBtn')
    },
    mandatory: true,
    showCounter: true,
    maxLength: 40,
    errorState: false,
    errorMessage: null,
    ourMask: {
        parseInputText: value => { return removeAccents(value) },
        validatorFn: value => { return noSpecialChar(value) }
    }
})

const merchantNameValidation = () => {
    if (!!props.formData.merchantName && props.formData.merchantName.length < 5) {
        merchantNameParams.value.errorState = true
        merchantNameParams.value.errorMessage = t('genericInputLengthErrorMessage', { minValue: 5, maxValue: 40 })
    }
}

/**********************************************************************
*                             Merchant Email                          *
**********************************************************************/
const merchantEmailParams = ref({
    labelText: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.merchantEmailLabel'),
    placeholder: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.merchantEmailPlaceholder'),
    bottomMessage: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.merchantEmailBottomMessage'),
    mandatory: true,
    errorState: false,
    errorMessage: null
})

const merchantEmailValidation = () => {
    if (!!props.formData.merchantEmail.length && !emailValidator(props.formData.merchantEmail)) {
        merchantEmailParams.value.errorState = true
        merchantEmailParams.value.errorMessage = t('invalidEmailError')
    }
}

/**********************************************************************
*                        Acceptance Point Name                        *
**********************************************************************/
const acceptancePointNameParams = ref({
    labelText: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.acceptancePointNameLabel'),
    placeholder: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.acceptancePointNamePlaceholder'),
    bottomMessage: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.acceptancePointNameBottomMessage'),
    tooltipInfo: {
        text: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.acceptancePointNameTooltip'),
        size: 'medium',
        leftButtonText: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.tooltipLefttBtn'),
        rightButtonText: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.tooltipRightBtn')
    },
    mandatory: true,
    showCounter: true,
    maxLength: 30,
    errorState: false,
    errorMessage: null,
    ourMask: {
        parseInputText: value => { return removeAccents(value) },
        validatorFn: value => { return noSpecialChar(value) }
    }
})

const acceptancePointNameValidation = () => {
    if (!!props.formData.acceptancePointName && props.formData.acceptancePointName.length < 5) {
        acceptancePointNameParams.value.errorState = true
        acceptancePointNameParams.value.errorMessage = t('genericInputLengthErrorMessage', { minValue: 5, maxValue: 30 })
    }
}

/**********************************************************************
*                        Acceptance Point Msisdn                      *
**********************************************************************/
const acceptancePointMsisdnParams = ref({
    labelText: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.acceptancePointMsisdnLabel'),
    placeholder: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.acceptancePointMsisdnPlaceholder'),
    mandatory: true,
    maxLength: 14,
	mask: PHONE_NUMBER,
	errorState: false,
	errorMessage: null
})

const acceptancePointMsisdnValidation = () => {
    if (!!props.formData.acceptancePointMsisdn && !baiPhoneNumberValidator(props.formData.acceptancePointMsisdn)) {
        acceptancePointMsisdnParams.value.errorState = true
        acceptancePointMsisdnParams.value.errorMessage = t('phoneLengthOrError')
    }
}

/**********************************************************************
*                     Acceptance Point Notifications                  *
**********************************************************************/
const acceptancePointRadioGroupParams = ref({
	label: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.acceptancePointNotificationsLabel'),
	mandatory: true,
	errorState: false,
	errorMessage: null
})

const acceptancePointNotificationEnabledParams = computed(() => {
    return [
       {
            id: 1,
            text: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.yes'),
            selected: props.formData.acceptancePointNotificationEnabled === 1
       }, {
            id: 2,
            text: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.no'),
            selected: props.formData.acceptancePointNotificationEnabled === 2
       }
   ]
})

const canProceedParams = ref({
    label: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.acceptConditions', { link: 'https://ib.bancobai.ao/FILES/BAIPaga_Comerciante_TC.pdf', target: '_blank' }),
    errorState: false,
    errorMessage: null
})

const transactionsConditionsParams = ref({
    label: t('companyPayments.baiPagaMerchant.creatingMerchant.step1.transactionsConditions'),
    errorState: false,
    errorMessage: null
})
/**********************************************************************
*                                                                     *
*                        MANDATORY FIELDS LOGIC                       *
*                                                                     *
***********************************************************************/
const allFormFields = ref({
    merchantUserNameParams: merchantUserNameParams.value,
    merchantUserEmailParams: merchantUserEmailParams.value,
    merchantNameParams: merchantNameParams.value,
    merchantEmailParams: merchantEmailParams.value,
    acceptancePointNameParams: acceptancePointNameParams.value,
    acceptancePointMsisdnParams: acceptancePointMsisdnParams.value,
	acceptancePointNotificationEnabledParams: acceptancePointRadioGroupParams.value,
    canProceedParams: canProceedParams.value,
    transactionsConditionsParams: transactionsConditionsParams.value
})

watch(() => props.showMandatoryErrors, newValue => {
	if (newValue) showMandatoryErrorsHandler()
    emit('resetShowMandatoryErrorsProp')
})

const showMandatoryErrorsHandler = () => {
	/* Logic to scroll to first emptyField */

	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(props.formData, props.mandatoryList)

	if (mandatoryFieldsEmptyList.length) {
        emit('scrollTo', getElementOffsetTopPosition(mandatoryFieldsEmptyList[0]))

        mandatoryFieldsEmptyList.forEach(fieldName => {
            allFormFields.value[`${fieldName}Params`].errorState = true
            allFormFields.value[`${fieldName}Params`].errorMessage = t('mandatoryFieldError')
        })
    }

    if (!props.formData.canProceed) {
        canProceedParams.value.errorState = true
        canProceedParams.value.errorMessage = t('mandatoryFieldError')
    }

    if (!props.formData.transactionsConditions) {
        transactionsConditionsParams.value.errorState = true
        transactionsConditionsParams.value.errorMessage = t('mandatoryFieldError')
    }
}
/**********************************************************************
*                                                                     *
*                          CLEAR ERRORS LOGIC                         *
*                                                                     *
***********************************************************************/
watch(() => props.clearFormErrors, () => {
    clearFormErrors()
    emit('formErrorsCleared')
})

const clearFieldError = (fieldName) => {
    allFormFields.value[fieldName].errorMessage = null
    allFormFields.value[fieldName].errorState = false
}

const clearFormErrors = () => {
    for (var fieldName in allFormFields.value) {
        clearFieldError(fieldName)
    }
}
</script>

<style lang="scss" scoped>
.message {
    @include H6-Regular;
    line-height: 18px;
    color: var(--secondary-dark-2);
}
</style>
